import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';
import { toast } from '../components/Toast.js';

const POLL_MS = 3000;

// Token-based public chat between an anonymous visitor and the property owner.
// Mirrors the imperative chat handling in ApplicationDetailView so polling does
// not destroy the input/scroll state.
export class InquiryView extends Component {
  constructor(ctx) {
    super(ctx);
    this.token = (ctx && ctx.params && ctx.params.token) || '';
    this.state = { loading: true, error: null, inquiry: null };
    this._timer = null;
    this._lastId = null;
    this._seen = new Set();
    this._bound = false;
    this._onVisibility = this._onVisibility.bind(this);
  }

  template() {
    if (this.state.loading) return `<section class="stack"><div class="muted">${esc(t('inquiries.loading'))}</div></section>`;
    if (this.state.error)   return `<section class="stack"><div class="card stack"><div class="error-banner">${esc(this.state.error)}</div></div></section>`;
    const inq = this.state.inquiry;
    const propName = (inq && inq.property && inq.property.name) || '';
    const slug = (inq && inq.property && inq.property.public_slug) || '';
    return `
      <section class="stack">
        ${slug ? `<a href="#/p/${esc(slug)}" class="btn btn-ghost">${icon('arrow-left', { size: 'sm' })}<span>${esc(t('inquiries.back_to_listing'))}</span></a>` : ''}
        <div class="card stack">
          <div class="row-between">
            <h2>${esc(propName)}</h2>
          </div>
          <div class="faint">${esc(t('inquiries.from'))}: ${esc(inq.visitor_name || '')} · ${esc(inq.visitor_email || '')}</div>
        </div>
        <div class="card stack" data-chat>
          <div class="row" style="gap:var(--space-2)">
            ${icon('send', { size: 'sm' })}<strong>${esc(t('inquiries.open_chat'))}</strong>
          </div>
          <div data-chat-list class="stack-2" style="max-height:420px;overflow-y:auto;padding:var(--space-2);background:var(--color-bg);border:1px solid var(--color-border);border-radius:var(--radius-md)"></div>
          <form class="row" style="gap:var(--space-2)" data-chat-form>
            <input class="input grow" type="text" data-chat-input maxlength="4000" placeholder="${esc(t('inquiries.chat.placeholder'))}" autocomplete="off" />
            <button class="btn btn-primary" type="submit" data-chat-send>
              ${icon('send', { size: 'sm' })}<span>${esc(t('inquiries.chat.send'))}</span>
            </button>
          </form>
        </div>
      </section>
    `;
  }

  mount() {
    if (this.state.loading) { this._load(); return; }
    if (this.state.error || !this.state.inquiry) return;
    const form = this.el.querySelector('[data-chat-form]');
    if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this._send(); });
    this._lastId = null;
    this._seen = new Set();
    this._poll(true);
    if (!this._timer) this._timer = setInterval(() => this._poll(), POLL_MS);
    if (!this._bound) {
      document.addEventListener('visibilitychange', this._onVisibility);
      this._bound = true;
    }
  }

  unmount() {
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
    if (this._bound) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._bound = false;
    }
  }

  _onVisibility() {
    if (document.hidden) {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
    } else if (!this._timer && this.state.inquiry) {
      this._poll();
      this._timer = setInterval(() => this._poll(), POLL_MS);
    }
  }

  async _load() {
    try {
      const inquiry = await api.get(`/public/inquiries/${encodeURIComponent(this.token)}`);
      this.setState({ inquiry, loading: false });
    } catch (e) {
      // A 404 means the stored token no longer maps to an inquiry; drop it
      // from localStorage so the next click on /p/{slug} opens a fresh modal
      // instead of bouncing the visitor back to a dead chat URL.
      if (e instanceof ApiError && e.status === 404) this._forgetStoredToken();
      const msg = e instanceof ApiError && e.status === 404
        ? t('inquiries.not_found')
        : (e instanceof ApiError ? e.message : t('inquiries.not_found'));
      this.setState({ error: msg, loading: false });
    }
  }

  _forgetStoredToken() {
    try {
      const prefix = 'nestly.inquiry.';
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(prefix) && localStorage.getItem(k) === this.token) {
          localStorage.removeItem(k);
        }
      }
    } catch (_) { /* ignore */ }
  }

  async _poll(initial = false) {
    if (document.hidden) return;
    try {
      const qs = this._lastId ? `?since=${encodeURIComponent(this._lastId)}` : '';
      const res = await api.get(`/public/inquiries/${encodeURIComponent(this.token)}/messages${qs}`);
      const items = Array.isArray(res.data) ? res.data : [];
      if (items.length > 0) this._append(items, initial);
    } catch (_) { /* ignore */ }
  }

  _append(items, initial) {
    const list = this.el.querySelector('[data-chat-list]');
    if (!list) return;
    const wasNearBottom = (list.scrollHeight - list.scrollTop - list.clientHeight) < 80;
    let appended = 0;
    for (const m of items) {
      if (this._seen.has(m.id)) continue;
      this._seen.add(m.id);
      this._lastId = m.id;
      const mine = m.sender_kind === 'VISITOR';
      const who = mine ? t('inquiries.you') : t('inquiries.landlord');
      const row = document.createElement('div');
      row.className = 'chat-msg';
      row.style.cssText = 'display:flex;flex-direction:column;align-items:' + (mine ? 'flex-end' : 'flex-start') + ';gap:2px';
      const bubbleStyle = mine
        ? 'background:var(--color-primary-soft);color:var(--color-primary);border:1px solid var(--color-primary-soft)'
        : 'background:var(--color-bg-card);color:var(--color-text);border:1px solid var(--color-border)';
      row.innerHTML = `
        <div class="faint" style="font-size:0.75rem">${esc(who)} · ${esc(m.created_at)}</div>
        <div class="chat-bubble" style="max-width:80%;padding:6px 10px;border-radius:var(--radius-md);${bubbleStyle};white-space:pre-wrap;word-wrap:break-word">${esc(m.body)}</div>`;
      list.appendChild(row);
      appended++;
    }
    if (initial || wasNearBottom) list.scrollTop = list.scrollHeight;
  }

  async _send() {
    const input = this.el.querySelector('[data-chat-input]');
    const btn = this.el.querySelector('[data-chat-send]');
    if (!input) return;
    const body = (input.value || '').trim();
    if (!body) return;
    input.disabled = true;
    if (btn) btn.disabled = true;
    try {
      const msg = await api.post(`/public/inquiries/${encodeURIComponent(this.token)}/messages`, { body });
      input.value = '';
      if (msg && msg.id) this._append([msg], false);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : t('inquiries.errors.send'));
    } finally {
      input.disabled = false;
      if (btn) btn.disabled = false;
      input.focus();
    }
  }
}
